// src/page/SupervisorRegistration.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Alert,
    Badge,
    Paper,
    Divider,
    useMediaQuery,
    useTheme,
    InputAdornment,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Fade,
    Zoom,
    Container,
    Card,
    CardContent,
    CircularProgress,
    LinearProgress,
    FormHelperText,
    IconButton,
    Grid,
    Autocomplete,
    Avatar,
    FormControlLabel,
    Switch
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Person,
    Email,
    Phone,
    School,
    Flag,
    Lock,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Cancel,
    Search,
    Login,
    ArrowBack,
    DoneAll,
    Send,
    VerifiedUser,
    ArrowBackIos,
    Email as EmailIcon,
    AccountCircle,
    MenuBook,
    CheckCircleOutlineRounded,
    LocalHospital,
    Star,
    Delete,
    Language
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { authService } from '../apiServices/authService';
import { APIUniversityNames, APISpecialtyList } from '../apiServices/APIs';
import { logoIcon } from '../assets';
import { alpha as muiAlpha } from '@mui/material/styles';

// Validation constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^\d{10}$/;

// Academic titles
const academicTitles = [
    { value: 'dr', label: 'Dr.' },
    { value: 'prof', label: 'Prof.' },
    { value: 'assoc_prof', label: 'Associate Prof.' },
    { value: 'assist_prof', label: 'Assistant Prof.' },
    { value: 'senior_lecturer', label: 'Senior Lecturer' },
    { value: 'lecturer', label: 'Lecturer' }
];

// Styled Components
const ModernCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const LogoImage = styled('img')(({ theme }) => ({
    height: 80,
    width: 80,
    borderRadius: 12,
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 'bold',
    py: 1.2,
    borderRadius: 2,
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
    '&:hover': {
        transform: 'translateY(-2px)',
    }
}));

// Success Page Component
const SuccessPage = ({ userData, onRedirect }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [countdown, setCountdown] = useState(10);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onRedirect();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [onRedirect]);

    const getAcademicTitleLabel = (value) => {
        const title = academicTitles.find(t => t.value === value);
        return title ? title.label : value;
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${theme.palette.common.auth}`,
                padding: isMobile ? 2 : 3,
            }}
        >
            <Container maxWidth="md">
                <Zoom in={true} timeout={800}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 3 : 5 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Zoom in={true} timeout={1000}>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 80 : 100,
                                            height: isMobile ? 80 : 100,
                                            margin: '0 auto',
                                            background: 'linear-gradient(135deg, #10B981, #059669)',
                                            animation: 'pulse 2s infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
                                                },
                                                '70%': {
                                                    transform: 'scale(1)',
                                                    boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)',
                                                },
                                                '100%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
                                                },
                                            },
                                        }}
                                    >
                                        <CheckCircleOutlineRounded sx={{ fontSize: isMobile ? 50 : 60 }} />
                                    </Avatar>
                                </Zoom>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #10B981, #059669)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                    }}
                                >
                                    Research Supervisor Registration Successful!
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                    Welcome to the SJC ScholarFlow Research Supervisor Community
                                </Typography>

                                <Chip
                                    icon={<VerifiedUser />}
                                    label="Research Supervisor Account Activated"
                                    color="success"
                                    sx={{ mt: 1, mb: 2 }}
                                />
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #86efac',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: 'bold',
                                        color: '#166534',
                                    }}
                                >
                                    <AccountCircle />
                                    Research Supervisor Account Details
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Person color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Full Name
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.full_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <EmailIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email Address
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.email || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Phone color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Contact Number
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.contact_phone || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <School color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    University
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.university_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <MenuBook color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Academic Title
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {getAcademicTitleLabel(userData?.academic_title) || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Flag color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Nationality
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.nationality || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #93c5fd',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: 'bold',
                                        color: '#1e40af',
                                    }}
                                >
                                    <VerifiedUser />
                                    Next Steps for Research Supervisors
                                </Typography>

                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Complete your research supervisor profile with research interests
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Set your availability for student supervision
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Review and respond to student supervision requests across universities
                                    </Typography>
                                </Box>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #93c5fd',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <CheckCircle color="success" />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1 }}>
                                            <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Account Activated
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1e3a8a', lineHeight: 1.6 }}>
                                            Your research supervisor account has been automatically activated. You can now log in and start 
                                            supervising students on the SJC ScholarFlow platform immediately.
                                            <br />
                                            <strong>No email verification required.</strong>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Redirecting to login page in {countdown} seconds...
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(countdown / 10) * 100}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        mb: 2,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(90deg, #10B981, #059669)',
                                            borderRadius: 3,
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        onClick={onRedirect}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #10B981, #059669)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #059669, #047857)',
                                            },
                                        }}
                                    >
                                        Go to Login Now
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </ModernCard>
                </Zoom>
            </Container>
        </Box>
    );
};

const SupervisorRegistration = () => {
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const isSubmittingRef = useRef(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        contact_phone: '',
        title: '',
        nationality: '',
        university_id: '',
        university_name: '',
        gender: '',
        academic_title: '',
        password: '',
        password_confirmation: '',
        specialties: [],
        allow_cross_university: true
    });

    // UI state
    const [universities, setUniversities] = useState([]);
    const [availableSpecialties, setAvailableSpecialties] = useState([]);
    const [universitySearchInput, setUniversitySearchInput] = useState('');
    const [specialtySearchInput, setSpecialtySearchInput] = useState('');
    const [selectedSpecialtyToAdd, setSelectedSpecialtyToAdd] = useState(null);
    const [tempSpecialtyData, setTempSpecialtyData] = useState({
        years_of_experience: '',
        expertise_level: 'intermediate',
        notes: '',
        is_primary: false
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [registeredUserData, setRegisteredUserData] = useState(null);

    // Validation state
    const [errors, setErrors] = useState({
        full_name: '',
        email: '',
        contact_phone: '',
        title: '',
        nationality: '',
        university_id: '',
        gender: '',
        academic_title: '',
        password: '',
        password_confirmation: '',
        specialties: '',
        form: ''
    });

    // Step validation state
    const [stepValidations, setStepValidations] = useState({
        0: false, // Personal Info
        1: false, // Research Specialties
        2: false, // Professional Details
        3: false  // Security
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        validateCurrentStep();
    }, [formData, activeStep, tempSpecialtyData]);

    const fetchInitialData = async () => {
        setLoading(true);
        setError(null);
        try {
            const api = authService.getApi();
            
            const uniResponse = await api.get(APIUniversityNames);
            const uniData = Array.isArray(uniResponse.data.data) ? uniResponse.data.data : [];
            setUniversities(uniData);

            const specResponse = await api.get(APISpecialtyList);
            const specData = Array.isArray(specResponse.data.data) ? specResponse.data.data : [];
            setAvailableSpecialties(specData);

        } catch (error) {
            let errorMessage = "Failed to load initial data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = "CONNECTION_ERROR";
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const validateCurrentStep = () => {
        let isValid = true;
        const newStepValidations = { ...stepValidations };
        const newErrors = { ...errors };

        switch (activeStep) {
            case 0:
                newErrors.full_name = '';
                newErrors.contact_phone = '';
                newErrors.title = '';
                newErrors.nationality = '';
                newErrors.gender = '';

                if (!formData.full_name.trim()) {
                    newErrors.full_name = 'Full name is required';
                    isValid = false;
                }

                if (!formData.contact_phone.trim()) {
                    newErrors.contact_phone = 'Contact number is required';
                    isValid = false;
                } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
                    newErrors.contact_phone = 'Contact number must be exactly 10 digits';
                    isValid = false;
                }

                if (!formData.title) {
                    newErrors.title = 'Title is required';
                    isValid = false;
                }

                if (!formData.nationality.trim()) {
                    newErrors.nationality = 'Nationality is required';
                    isValid = false;
                }

                if (!formData.gender) {
                    newErrors.gender = 'Gender is required';
                    isValid = false;
                }
                break;
            
            case 1:
                newErrors.specialties = '';
                if (formData.specialties.length === 0) {
                    newErrors.specialties = 'Please add at least one research specialty';
                    isValid = false;
                } else {
                    const hasPrimary = formData.specialties.some(s => s.is_primary);
                    if (!hasPrimary) {
                        newErrors.specialties = 'Please mark at least one specialty as your primary focus';
                        isValid = false;
                    }
                }
                break;

            case 2:
                newErrors.university_id = '';
                newErrors.email = '';
                newErrors.academic_title = '';

                if (!formData.university_id) {
                    newErrors.university_id = 'University selection is required';
                    isValid = false;
                }

                if (!formData.email.trim()) {
                    newErrors.email = 'Email is required';
                    isValid = false;
                } else if (!EMAIL_REGEX.test(formData.email)) {
                    newErrors.email = 'Please enter a valid email address';
                    isValid = false;
                }

                if (!formData.academic_title) {
                    newErrors.academic_title = 'Academic title is required';
                    isValid = false;
                }
                break;
            
            case 3:
                newErrors.password = '';
                newErrors.password_confirmation = '';

                if (!formData.password) {
                    newErrors.password = 'Password is required';
                    isValid = false;
                }

                if (!formData.password_confirmation) {
                    newErrors.password_confirmation = 'Please confirm your password';
                    isValid = false;
                } else if (formData.password !== formData.password_confirmation) {
                    newErrors.password_confirmation = 'Passwords do not match';
                    isValid = false;
                }
                break;
            
            default:
                isValid = false;
        }

        newStepValidations[activeStep] = isValid;
        setStepValidations(newStepValidations);
        setErrors(newErrors);
        return isValid;
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        
        if (name === 'contact_phone') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name] || errors.form) {
            setErrors(prev => ({ ...prev, [name]: '', form: '' }));
        }
    };

    const handleTempSpecialtyChange = (e) => {
        const { name, value, checked, type } = e.target;
        setTempSpecialtyData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddSpecialty = () => {
        if (!selectedSpecialtyToAdd) {
            showToast("Validation Error", "Please select a specialty from the search list first", "warning");
            return;
        }

        const alreadyAdded = formData.specialties.some(s => s.specialty_id === selectedSpecialtyToAdd.id);
        if (alreadyAdded) {
            showToast("Error", "This specialty is already added to your list", "error");
            return;
        }

        if (formData.specialties.length >= 10) {
            showToast("Limit Reached", "You can only select up to 10 specialties", "warning");
            return;
        }

        let updatedSpecialties = [...formData.specialties];
        if (tempSpecialtyData.is_primary) {
            updatedSpecialties = updatedSpecialties.map(spec => ({
                ...spec,
                is_primary: false
            }));
        }

        const newSpecialty = {
            specialty_id: selectedSpecialtyToAdd.id,
            specialty_name: selectedSpecialtyToAdd.name,
            specialty_code: selectedSpecialtyToAdd.specialty_code,
            years_of_experience: tempSpecialtyData.years_of_experience || null,
            expertise_level: tempSpecialtyData.expertise_level,
            notes: tempSpecialtyData.notes || '',
            is_primary: tempSpecialtyData.is_primary
        };

        setFormData(prev => ({
            ...prev,
            specialties: [...updatedSpecialties, newSpecialty]
        }));

        setSelectedSpecialtyToAdd(null);
        setSpecialtySearchInput('');
        setTempSpecialtyData({
            years_of_experience: '',
            expertise_level: 'intermediate',
            notes: '',
            is_primary: false
        });

        showToast("Added", `Successfully added ${newSpecialty.specialty_name}`, "success");
    };

    const handleRemoveSpecialty = (specialtyId) => {
        setFormData(prev => ({
            ...prev,
            specialties: prev.specialties.filter(s => s.specialty_id !== specialtyId)
        }));
        showToast("Removed", "Specialty removed from registration list", "info");
    };

    const handleUniversitySelect = (event, selectedUniversity) => {
        if (selectedUniversity) {
            setFormData(prev => ({
                ...prev,
                university_id: selectedUniversity.id,
                university_name: selectedUniversity.name
            }));
            setErrors(prev => ({ ...prev, university_id: '' }));
            showToast("Success", `Selected: ${selectedUniversity.name}`, "success");
        } else {
            setFormData(prev => ({
                ...prev,
                university_id: '',
                university_name: ''
            }));
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = { ...errors, form: '' };

        if (!formData.full_name.trim()) {
            newErrors.full_name = 'Full name is required';
            valid = false;
        }

        if (!formData.contact_phone.trim()) {
            newErrors.contact_phone = 'Contact number is required';
            valid = false;
        } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
            newErrors.contact_phone = 'Contact number must be exactly 10 digits';
            valid = false;
        }

        if (!formData.title) {
            newErrors.title = 'Title is required';
            valid = false;
        }

        if (!formData.nationality.trim()) {
            newErrors.nationality = 'Nationality is required';
            valid = false;
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
            valid = false;
        }

        if (formData.specialties.length === 0) {
            newErrors.specialties = 'At least one research specialty is required';
            valid = false;
        } else if (!formData.specialties.some(s => s.is_primary)) {
            newErrors.specialties = 'At least one specialty must be set as primary';
            valid = false;
        }

        if (!formData.university_id) {
            newErrors.university_id = 'University selection is required';
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!EMAIL_REGEX.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!formData.academic_title) {
            newErrors.academic_title = 'Academic title is required';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
            valid = false;
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
            valid = false;
        }

        if (!valid) {
            newErrors.form = 'Please fix all validation errors before submitting';
        }

        setErrors(newErrors);
        return valid;
    };

    const resetForm = () => {
        setFormData({
            full_name: '',
            email: '',
            contact_phone: '',
            title: '',
            nationality: '',
            university_id: '',
            university_name: '',
            gender: '',
            academic_title: '',
            password: '',
            password_confirmation: '',
            specialties: [],
            allow_cross_university: true
        });
        setActiveStep(0);
        setUniversitySearchInput('');
        setSpecialtySearchInput('');
        setSelectedSpecialtyToAdd(null);
        setErrors({
            full_name: '',
            email: '',
            contact_phone: '',
            title: '',
            nationality: '',
            university_id: '',
            gender: '',
            academic_title: '',
            password: '',
            password_confirmation: '',
            specialties: '',
            form: ''
        });
    };

    const handleNextStep = () => {
        if (validateCurrentStep()) {
            setActiveStep(prev => Math.min(prev + 1, 3));
            showToast("Step Complete", `Step ${activeStep + 1} completed successfully`, "success");
        } else {
            showToast("Validation Error", "Please complete all fields in the current step correctly", "warning");
        }
    };

    const handlePreviousStep = () => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    };
    
    const handleGoBack = () => {
        navigate('/register');
        showToast("Navigation", "Returning to registration options", "info");
    };
    
    const handleLogIn = () => {
        navigate('/');
        showToast("Navigation", "Redirecting to login page", "info");
    };

    const handleRedirectToLogin = () => {
        navigate('/login');
    };
    
    const handleSubmit = async () => {
        // Prevent concurrent double submissions synchronously
        if (isSubmitting || isSubmittingRef.current) {
            return;
        }
        if (!validateForm()) {
            showToast("Validation Error", "Please fix all validation errors before submitting", "error");
            return;
        }

        setIsSubmitting(true);
        showToast("Processing", "Creating your research supervisor account...", "info");

        try {
            const registrationData = {
                full_name: formData.full_name,
                email: formData.email,
                title: formData.title,
                university_id: formData.university_id,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
                contact_phone: formData.contact_phone,
                nationality: formData.nationality,
                gender: formData.gender,
                academic_title: formData.academic_title,
                specialties: formData.specialties,
                allow_cross_university: formData.allow_cross_university
            };

            const response = await authService.registerSupervisor(registrationData);

            if (response.status === "success") {
                setRegisteredUserData({
                    full_name: formData.full_name,
                    email: formData.email,
                    contact_phone: formData.contact_phone,
                    university_name: formData.university_name,
                    nationality: formData.nationality,
                    title: formData.title,
                    gender: formData.gender,
                    academic_title: formData.academic_title
                });
                
                setShowSuccessPage(true);
                resetForm();

                showToast(
                    "Success", 
                    "Your research supervisor account has been created and activated successfully!",
                    "success"
                );
            } else {
                showToast("Error", response.message || "Registration failed", "error");
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            if (error.errors) {
                let errorMessages = [];
                for (const field in error.errors) {
                    errorMessages.push(...error.errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else if (error.message) {
                showToast("Error", error.message, "error");
            } else {
                showToast("Error", "An unexpected error occurred during registration", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const steps = ['Personal Information', 'Research Specialties', 'Professional Details', 'Security Setup'];

    if (showSuccessPage) {
        return (
            <SuccessPage 
                userData={registeredUserData} 
                onRedirect={handleRedirectToLogin}
            />
        );
    }

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="h6" color="primary" sx={{ ml: 2 }}>
                Loading system configurations...
            </Typography>
        </Box>
    );

    return (
        <Box sx={{background: `${theme.palette.common.auth}`}}>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Zoom in={true}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <LogoImage src={logoIcon} alt="SJC ScholarFlow" />
                                <Typography 
                                    variant="h4" 
                                    gutterBottom 
                                    sx={{ 
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                        background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontWeight: 'bold',
                                        mt: 2
                                    }}
                                >
                                    Become a Research Supervisor
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Join SJC ScholarFlow to mentor the next generation of researchers
                                </Typography>
                            </Box>

                            <Stepper activeStep={activeStep} sx={{ mb: 4, overflowX: 'auto' }}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }} icon={<Cancel />}>
                                    <Typography variant="subtitle1">Error: {error}</Typography>
                                </Alert>
                            )}

                            <Box component="div">
                                {/* Step 1: Personal Information */}
                                <Fade in={activeStep === 0} timeout={500}>
                                    <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                            <FormControl fullWidth error={!!errors.title}>
                                                <InputLabel>Title *</InputLabel>
                                                <Select
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    label="Title *"
                                                >
                                                    <MenuItem value="">Select Title</MenuItem>
                                                    <MenuItem value="Mr">Mr</MenuItem>
                                                    <MenuItem value="Mrs">Mrs</MenuItem>
                                                    <MenuItem value="Ms">Ms</MenuItem>
                                                    <MenuItem value="Dr">Dr</MenuItem>
                                                    <MenuItem value="Prof">Prof</MenuItem>
                                                </Select>
                                                {errors.title && (
                                                    <FormHelperText>{errors.title}</FormHelperText>
                                                )}
                                            </FormControl>

                                            <TextField
                                                name="full_name"
                                                label="Full Name *"
                                                value={formData.full_name}
                                                onChange={handleInputChange}
                                                fullWidth
                                                error={!!errors.full_name}
                                                helperText={errors.full_name}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Person color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            <TextField
                                                name="contact_phone"
                                                label="Contact Phone *"
                                                value={formData.contact_phone}
                                                onChange={handleInputChange}
                                                fullWidth
                                                error={!!errors.contact_phone}
                                                helperText={errors.contact_phone || "Must be exactly 10 digits"}
                                                placeholder="0712345678"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Phone color="action" />
                                                        </InputAdornment>
                                                    ),
                                                    inputProps: {
                                                        maxLength: 10,
                                                        pattern: "[0-9]*",
                                                        inputMode: "numeric"
                                                    }
                                                }}
                                            />

                                            <TextField
                                                name="nationality"
                                                label="Nationality *"
                                                value={formData.nationality}
                                                onChange={handleInputChange}
                                                fullWidth
                                                error={!!errors.nationality}
                                                helperText={errors.nationality}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Flag color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            <FormControl fullWidth error={!!errors.gender}>
                                                <InputLabel>Gender *</InputLabel>
                                                <Select
                                                    name="gender"
                                                    value={formData.gender}
                                                    onChange={handleInputChange}
                                                    label="Gender *"
                                                >
                                                    <MenuItem value="">Select Gender</MenuItem>
                                                    <MenuItem value="male">Male</MenuItem>
                                                    <MenuItem value="female">Female</MenuItem>
                                                    <MenuItem value="other">Other</MenuItem>
                                                </Select>
                                                {errors.gender && (
                                                    <FormHelperText>{errors.gender}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </Fade>

                                {/* Step 2: Research Specialties Selection */}
                                <Fade in={activeStep === 1} timeout={500}>
                                    <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocalHospital color="primary" /> Select Your Research Specialties
                                        </Typography>

                                        {errors.specialties && (
                                            <Alert severity="error" sx={{ mb: 2 }}>{errors.specialties}</Alert>
                                        )}

                                        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                                            <Autocomplete
                                                id="specialty-search"
                                                options={availableSpecialties}
                                                value={selectedSpecialtyToAdd}
                                                onChange={(event, val) => setSelectedSpecialtyToAdd(val)}
                                                inputValue={specialtySearchInput}
                                                onInputChange={(e, val) => setSpecialtySearchInput(val)}
                                                getOptionLabel={(option) => `${option.name} (${option.specialty_code || 'No Code'})`}
                                                renderInput={(params) => {
                                                    const { InputProps, ...restParams } = params;
                                                    return (
                                                        <TextField
                                                            {...restParams}
                                                            label="Search Specialty to Add *"
                                                            placeholder="Type here to search specialties..."
                                                            InputProps={{
                                                                ...InputProps,
                                                                startAdornment: (
                                                                    <>
                                                                        <InputAdornment position="start">
                                                                            <Search color="action" />
                                                                        </InputAdornment>
                                                                        {InputProps?.startAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />

                                            {selectedSpecialtyToAdd && (
                                                <Zoom in={true}>
                                                    <Box sx={{ mt: 3, p: 2, background: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                            Configure Details for: {selectedSpecialtyToAdd.name}
                                                        </Typography>
                                                        
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Years of Experience"
                                                                    name="years_of_experience"
                                                                    type="number"
                                                                    value={tempSpecialtyData.years_of_experience}
                                                                    onChange={handleTempSpecialtyChange}
                                                                    inputProps={{ min: 0, max: 50 }}
                                                                />
                                                            </Grid>
                                                            
                                                            <Grid item xs={12} sm={6}>
                                                                <FormControl fullWidth>
                                                                    <InputLabel>Expertise Level</InputLabel>
                                                                    <Select
                                                                        name="expertise_level"
                                                                        value={tempSpecialtyData.expertise_level}
                                                                        onChange={handleTempSpecialtyChange}
                                                                        label="Expertise Level"
                                                                    >
                                                                        <MenuItem value="beginner">Beginner</MenuItem>
                                                                        <MenuItem value="intermediate">Intermediate</MenuItem>
                                                                        <MenuItem value="expert">Expert</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Specialty Notes (Optional)"
                                                                    name="notes"
                                                                    placeholder="Specify core focus areas within this domain..."
                                                                    value={tempSpecialtyData.notes}
                                                                    onChange={handleTempSpecialtyChange}
                                                                    multiline
                                                                    rows={2}
                                                                />
                                                            </Grid>

                                                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            name="is_primary"
                                                                            checked={tempSpecialtyData.is_primary}
                                                                            onChange={handleTempSpecialtyChange}
                                                                            color="primary"
                                                                            disabled={formData.specialties.some(s => s.is_primary) && !tempSpecialtyData.is_primary}
                                                                        />
                                                                    }
                                                                    label="Set as Primary Specialty"
                                                                />
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleAddSpecialty}
                                                                    startIcon={<DoneAll />}
                                                                >
                                                                    Add to List
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Zoom>
                                            )}
                                        </Paper>

                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            Selected Specialties ({formData.specialties.length}/10)
                                        </Typography>

                                        {formData.specialties.length === 0 ? (
                                            <Alert severity="info" sx={{ py: 2 }}>
                                                No specialties added yet. Please search and configure specialties above to add them to your registration application.
                                            </Alert>
                                        ) : (
                                            <Grid container spacing={2}>
                                                {formData.specialties.map((item) => (
                                                    <Grid item xs={12} sm={6} key={item.specialty_id}>
                                                        <Card variant="outlined" sx={{ position: 'relative', background: item.is_primary ? '#eff6ff' : 'inherit', border: item.is_primary ? '1px solid #bfdbfe' : '1px solid #e2e8f0' }}>
                                                            {item.is_primary && (
                                                                <Chip
                                                                    icon={<Star />}
                                                                    label="Primary Focus"
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                                                />
                                                            )}
                                                            <CardContent sx={{ pr: 6 }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                    {item.specialty_name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                    Code: {item.specialty_code || 'N/A'}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                                    <Chip label={`Level: ${item.expertise_level}`} size="small" variant="outlined" />
                                                                    {item.years_of_experience && (
                                                                        <Chip label={`${item.years_of_experience} yrs exp`} size="small" variant="outlined" />
                                                                    )}
                                                                </Box>
                                                                {item.notes && (
                                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>
                                                                        "{item.notes}"
                                                                    </Typography>
                                                                )}
                                                            </CardContent>
                                                            <IconButton
                                                                onClick={() => handleRemoveSpecialty(item.specialty_id)}
                                                                sx={{ position: 'absolute', bottom: 8, right: 8, color: 'error.main' }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        )}
                                    </Box>
                                </Fade>

                                {/* Step 3: Professional Details */}
                                <Fade in={activeStep === 2} timeout={500}>
                                    <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
                                        <Box sx={{ mb: 3 }}>
                                            <Autocomplete
                                                id="university-select"
                                                options={universities}
                                                loading={loading}
                                                value={universities.find(u => u.id === formData.university_id) || null}
                                                onChange={handleUniversitySelect}
                                                inputValue={universitySearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setUniversitySearchInput(newInputValue);
                                                }}
                                                getOptionLabel={(option) => `${option.name} ${option.code ? `(${option.code})` : ''}`}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                <strong>{option.name}</strong>
                                                            </Typography>
                                                            {option.code && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code: {option.code}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={(params) => {
                                                    const { InputProps, ...restParams } = params;
                                                    return (
                                                        <TextField
                                                            {...restParams}
                                                            label="Primary Affiliated University *"
                                                            required
                                                            error={!!errors.university_id}
                                                            helperText={errors.university_id || "Select your primary institutional employer"}
                                                            InputProps={{
                                                                ...InputProps,
                                                                startAdornment: (
                                                                    <>
                                                                        <InputAdornment position="start">
                                                                            <School color="action" />
                                                                        </InputAdornment>
                                                                        {InputProps?.startAdornment}
                                                                    </>
                                                                ),
                                                                endAdornment: (
                                                                    <>
                                                                        {loading && <CircularProgress color="inherit" size={20} />}
                                                                        <Badge 
                                                                            badgeContent={universities.length} 
                                                                            color="primary"
                                                                            sx={{ mr: 1 }}
                                                                        />
                                                                        {InputProps?.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Box>

                                        <FormControl fullWidth error={!!errors.academic_title} sx={{ mb: 3 }}>
                                            <InputLabel>Academic Title *</InputLabel>
                                            <Select
                                                name="academic_title"
                                                value={formData.academic_title}
                                                onChange={handleInputChange}
                                                label="Academic Title *"
                                            >
                                                <MenuItem value="">Select Academic Title</MenuItem>
                                                {academicTitles.map((title) => (
                                                    <MenuItem key={title.value} value={title.value}>
                                                        {title.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.academic_title && (
                                                <FormHelperText>{errors.academic_title}</FormHelperText>
                                            )}
                                        </FormControl>

                                        <TextField
                                            name="email"
                                            label="Email Address *"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ mb: 3 }}
                                        />

                                        {/* No Email Verification Required Info */}
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                mb: 3,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)',
                                                border: '1px solid',
                                                borderColor: 'primary.100',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <CheckCircle color="success" sx={{ mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.dark', mb: 1 }}>
                                                        <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                        No Email Verification Required
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                                        Your research supervisor account will be automatically activated upon registration. 
                                                        You can log in immediately after completing the registration process.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>

                                        {/* Cross-institutional assignment switch */}
                                        <Paper variant="outlined" sx={{ p: 2, background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 2, mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Language color="primary" />
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                            Cross-University Supervision Mode
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: '450px' }}>
                                                            Allows you to accept supervision requests and access students belonging to other universities within the ScholarFlow network.
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Switch
                                                    name="allow_cross_university"
                                                    checked={formData.allow_cross_university}
                                                    onChange={handleInputChange}
                                                    color="primary"
                                                />
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Fade>

                                {/* Step 4: Security Setup */}
                                <Fade in={activeStep === 3} timeout={500}>
                                    <Box sx={{ display: activeStep === 3 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                        {/* Password Field */}
                                        <TextField
                                            name="password"
                                            label="Password *"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.password}
                                            helperText={errors.password || "Can be simple: words, letters, or numbers"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="action" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton
                                                            type="button"
                                                            onClick={() => setShowPassword((prev) => !prev)}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            title={showPassword ? "Hide password" : "Show password"}
                                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                                            sx={{
                                                                color: showPassword ? 'primary.main' : '#757575',
                                                                padding: '8px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '40px',
                                                                minHeight: '40px',
                                                                cursor: 'pointer',
                                                                transition: 'color 0.2s ease',
                                                                '&:hover': {
                                                                    color: 'primary.dark'
                                                                }
                                                            }}
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            ) : (
                                                                <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {/* Confirm Password Field */}
                                        <TextField
                                            name="password_confirmation"
                                            label="Confirm Password *"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.password_confirmation}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.password_confirmation}
                                            helperText={errors.password_confirmation}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="action" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton
                                                            type="button"
                                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            title={showConfirmPassword ? "Hide password" : "Show password"}
                                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                            sx={{
                                                                color: showConfirmPassword ? 'primary.main' : '#757575',
                                                                padding: '8px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '40px',
                                                                minHeight: '40px',
                                                                cursor: 'pointer',
                                                                transition: 'color 0.2s ease',
                                                                '&:hover': {
                                                                    color: 'primary.dark'
                                                                }
                                                            }}
                                                        >
                                                            {showConfirmPassword ? (
                                                                <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            ) : (
                                                                <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Fade>

                                {errors.form && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="error">{errors.form}</Alert>
                                    </Box>
                                )}

                                {/* Bottom Centered Step Navigation Buttons (Back & Next) */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                                    {activeStep > 0 && (
                                        <Button 
                                            variant="outlined"
                                            onClick={handlePreviousStep}
                                            startIcon={<ArrowBack />}
                                            disabled={isSubmitting}
                                            sx={{ minWidth: 130, py: 1.2, borderRadius: 2 }}
                                        >
                                            Back
                                        </Button>
                                    )}
                                    {activeStep < 3 && (
                                        <Button 
                                            variant="contained"
                                            onClick={handleNextStep}
                                            disabled={!stepValidations[activeStep]}
                                            sx={{ minWidth: 130, py: 1.2, borderRadius: 2 }}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </Box>

                                {/* Step 0 Additional Navigation Actions */}
                                {activeStep === 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={2} justifyContent="center">
                                            <Grid item xs={12} sm={6}>
                                                <ActionButton
                                                    fullWidth
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={<ArrowBackIos />}
                                                    onClick={handleGoBack}
                                                    sx={{
                                                        borderColor: '#FF6B6B',
                                                        color: '#FF6B6B',
                                                        borderRadius: 20,
                                                        '&:hover': {
                                                            borderColor: '#FF6B6B',
                                                            backgroundColor: muiAlpha('#FF6B6B', 0.1),
                                                        }
                                                    }}
                                                >
                                                    Back to Registration Options
                                                </ActionButton>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <ActionButton
                                                    fullWidth
                                                    variant="contained"
                                                    startIcon={<Login />}
                                                    onClick={handleLogIn}
                                                    sx={{
                                                        borderRadius: 20,
                                                        background: 'linear-gradient(135deg, #10B981, #059669)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #059669, #047857)',
                                                        }
                                                    }}
                                                >
                                                    Sign In to Existing Account
                                                </ActionButton>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Step 4 Final Registration Action */}
                                {activeStep === 3 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            disabled={isSubmitting || !stepValidations[3]}
                                            fullWidth
                                            onClick={handleSubmit}
                                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                            sx={{ 
                                                py: 1.5, 
                                                fontSize: '1.1rem',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    Creating Your Account...
                                                </Box>
                                            ) : (
                                                'Register as Research Supervisor'
                                            )}
                                        </Button>

                                        {isSubmitting && (
                                            <Box sx={{ width: '100%', mt: 1 }}>
                                                <LinearProgress
                                                    sx={{ 
                                                        height: 4,
                                                        borderRadius: 2
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                                    Setting up your research supervisor account and registering research fields...
                                                </Typography>
                                            </Box>
                                        )}

                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="large"
                                            fullWidth
                                            sx={{ mt: 2 }}
                                            onClick={() => navigate('/login')}
                                            startIcon={<Login />}
                                            disabled={isSubmitting}
                                        >
                                            Already have an account? Sign In
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </ModernCard>
                </Zoom>
            </Container>
        </Box>
    );
};

export default SupervisorRegistration;